import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useEffect } from 'react';
import { slugify, uniqueId, getDataAttributes } from '@nicollet/common';
import { ErrorMessage } from '../ErrorMessage/index.js';
import { RadioOuterWrap, RadioInput, RadioWrap, RadioVisual, RadioLabel, RadioMicrocopy } from './styles.js';

const Radio = forwardRef(({
  autoComplete,
  autoFocus,
  checked,
  children,
  disableLiveRegion,
  disabled,
  errorText,
  hasActive,
  hasFocus,
  hasHover,
  hideLabel,
  isValid,
  microcopy,
  name = 'default-name-change-me',
  noAnimation,
  onBlur,
  onChange,
  onFocus,
  readOnly,
  required,
  screenReader,
  value,
  microcopyPlacement = 'bottom',
  ...props
}, ref) => {
  /* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */
  const label = props.label || children || 'Select me!';
  const className = props.className ? `${props.className} nds-radio` : 'nds-radio';
  /*
   * Make sure we choose a provided or generated unique ID only once, not each render, OR when the
   * id or label changes
   */

  const [id, setId] = useState('');
  useEffect(() => {
    /* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */
    setId(props.id || slugify(uniqueId(label)) || 'default-id-change-me');
  }, [label, props.id, props.label]);

  if (process.env.NODE_ENV === 'development' && (!id || id === '')) {
    // eslint-disable-next-line no-console
    console.warn("Warning: No 'id' prop provided for Radio. This will become a required prop for Radio in the upcoming Nicollet v8 release.");
  }

  const htmlAttributes = {
    autoComplete,
    autoFocus,
    className,
    disabled,
    id,
    name,
    onBlur,
    onChange,
    onFocus,
    required,
    value
  };
  return jsxs(RadioOuterWrap, Object.assign({
    className: className
  }, getDataAttributes({ ...props
  }), {
    children: [jsx(RadioInput, Object.assign({}, htmlAttributes, {
      checked: checked,
      readOnly: readOnly,
      ref: ref,
      type: "radio"
    }), void 0), jsxs(RadioWrap, Object.assign({
      hasFocus: hasFocus,
      hasHover: hasHover,
      htmlFor: id,
      microcopyPlacement: microcopyPlacement
    }, {
      children: [jsx(RadioVisual, {
        hasActive: hasActive,
        hasHover: hasHover,
        hiddenLabel: hideLabel,
        htmlFor: id !== null && id !== void 0 ? id : 'default-id-change-me',
        noAnimation: noAnimation,
        valid: isValid
      }, void 0), jsx(RadioLabel, {
        disabled: disabled,
        disableLiveRegion: disableLiveRegion,
        errorText: errorText,
        hasActive: hasActive,
        hasHover: hasHover,
        hidden: hideLabel,
        label: label,
        microcopy: microcopy,
        screenReader: screenReader,
        tag: "span",
        valid: isValid
      }, void 0), microcopy && jsx(RadioMicrocopy, Object.assign({
        "aria-hidden": "true",
        microcopyPlacement: microcopyPlacement
      }, {
        children: microcopy
      }), void 0)]
    }), void 0), isValid === false && errorText && jsx(ErrorMessage, Object.assign({
      "aria-hidden": "true"
    }, {
      children: errorText
    }), void 0)]
  }), void 0);
});
Radio.defaultProps = {
  autoFocus: false,
  checked: false,
  disabled: false,
  hideLabel: false,
  required: false
};
Radio.displayName = 'Radio';

export { Radio };
